/**
 * Demo / preview mode. Activated with `?mock=full` in dev. Stubs the
 * entire `fetch` surface so the app renders MainLayout with realistic
 * fake data — no backend needed. Used to let designers + reviewers tap
 * around the UI without spinning up Postgres / Redis / MinIO.
 *
 * Production builds skip this entirely (`import.meta.env.DEV` gate).
 */

import { DEFAULT_MEMBER_PERMISSIONS, PERMISSIONS } from '@/lib/permissions';
import { createMockGuildSettings } from '@/lib/mockGuildSettings';
import { handleMockMutes } from '@/lib/mockMutes';
import { mockReportQueue } from '@/lib/mockReports';
import { handleMockAccount } from '@/lib/mockAccount';
import { BIG_GUILD_ID, makeBigServer } from '@/lib/mockBigServer';

interface MockUser {
  showTeamInDms?: boolean;
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  teamTag?: string | null;
  status: 'ONLINE' | 'IDLE' | 'DND' | 'OFFLINE' | 'INVISIBLE';
}

const ME: MockUser = {
  showTeamInDms: true,
  id: 'me_0',
  platformId: 'p_me_0',
  username: 'tester',
  displayName: 'Тестер',
  avatarUrl: null,
  teamTag: 'NAVI',
  status: 'ONLINE',
};

const PEERS: MockUser[] = [
  { id: 'u_kostyl', platformId: 'p_kostyl', username: 'kostyl', displayName: 'Костыль', avatarUrl: null, teamTag: 'VP', status: 'ONLINE' },
  { id: 'u_b1ad', platformId: 'p_b1ad', username: 'b1ad', displayName: 'b1ad', avatarUrl: null, teamTag: 'NAVI', status: 'ONLINE' },
  { id: 'u_makes', platformId: 'p_makes', username: 'makes', displayName: 'maks', avatarUrl: null, status: 'IDLE' },
  { id: 'u_dmnk', platformId: 'p_dmnk', username: 'domnik', displayName: 'Доминик', avatarUrl: null, teamTag: 'C9', status: 'DND' },
  { id: 'u_jamo', platformId: 'p_jamo', username: 'jamo', displayName: 'Джамо', avatarUrl: null, status: 'OFFLINE' },
];

const NOW = Date.now();
const MIN = 60_000;

function makeGuild() {
  return {
    id: 'g_cispracs',
    name: 'cispracs · core',
    iconUrl: null,
    ownerId: 'me_0',
    region: 'ru',
    createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(),
    updatedAt: new Date(NOW - 24 * 3600_000).toISOString(),
    channels: [
      { id: 'cat_text', guildId: 'g_cispracs', type: 'CATEGORY', name: 'ТЕКСТ', topic: null, position: 0, parentId: null, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
      { id: 'c_general', guildId: 'g_cispracs', type: 'TEXT', name: 'general', topic: 'Общий чат комьюнити. Без флуда.', position: 1, parentId: 'cat_text', slowmodeS: 0, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
      { id: 'c_praks', guildId: 'g_cispracs', type: 'TEXT', name: 'praks', topic: 'Праки и связки. Делимся CFG и видосами.', position: 2, parentId: 'cat_text', slowmodeS: 10, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
      { id: 'c_lfg', guildId: 'g_cispracs', type: 'TEXT', name: 'lfg', topic: 'Ищем тиммейтов на 5×5 mm / faceit.', position: 3, parentId: 'cat_text', slowmodeS: 0, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
      { id: 'cat_voice', guildId: 'g_cispracs', type: 'CATEGORY', name: 'ГОЛОС', topic: null, position: 4, parentId: null, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
      { id: 'c_main_voice', guildId: 'g_cispracs', type: 'VOICE', name: 'main voice', position: 5, parentId: 'cat_voice', bitrateKbps: 96, userLimit: 0, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
      { id: 'c_5v5', guildId: 'g_cispracs', type: 'VOICE', name: '5×5', position: 6, parentId: 'cat_voice', bitrateKbps: 96, userLimit: 5, createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), updatedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString() },
    ],
  };
}

const GUILD = makeGuild();

/** Lightweight extra servers so the rail (reorder + folders) is demoable. */
function lightGuild(id: string, name: string) {
  const ts = new Date(NOW - 20 * 24 * 3600_000).toISOString();
  return {
    id,
    name,
    iconUrl: null,
    ownerId: 'me_0',
    region: 'ru',
    createdAt: ts,
    updatedAt: ts,
    channels: [
      { id: `${id}_general`, guildId: id, type: 'TEXT', name: 'general', topic: null, position: 0, parentId: null, slowmodeS: 0, createdAt: ts, updatedAt: ts },
    ],
  };
}

/**
 * A server where I am a plain member, not the owner: rights come from the
 * server's `myPermissions`, so the read-only announcements channel and the
 * stage I may not enter show those states in `?mock=full`.
 */
function memberGuild(id: string, name: string) {
  const base = lightGuild(id, name);
  const ts = base.createdAt;
  const member = DEFAULT_MEMBER_PERMISSIONS.toString();
  const readOnly = (PERMISSIONS.VIEW_CHANNEL | PERMISSIONS.READ_MESSAGE_HISTORY | PERMISSIONS.ADD_REACTIONS).toString();
  return {
    ...base,
    ownerId: 'p_orga',
    myPermissions: member,
    channels: [
      { ...base.channels[0]!, myPermissions: member },
      { id: `${id}_news`, guildId: id, type: 'ANNOUNCE', name: 'news', topic: 'Только для организаторов', position: 1, parentId: null, slowmodeS: 0, createdAt: ts, updatedAt: ts, myPermissions: readOnly },
      { id: `${id}_stage`, guildId: id, type: 'VOICE', name: 'stage', topic: null, position: 2, parentId: null, bitrateKbps: 96, userLimit: 0, createdAt: ts, updatedAt: ts, myPermissions: PERMISSIONS.VIEW_CHANNEL.toString() },
    ],
  };
}

/** `&scale=big` adds a server at full size (see mockBigServer). */
const BIG =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('scale') === 'big'
    ? makeBigServer(NOW)
    : null;

const GUILDS = [
  GUILD,
  lightGuild('g_faceit', 'FACEIT Hub'),
  lightGuild('g_navi', 'NaVi Fan'),
  memberGuild('g_major', 'Major 2026'),
  ...(BIG ? [BIG.guild] : []),
];

/** Mutable mock folder state so the drag-to-create / add / remove gestures
 *  actually work in `?mock=full` (no real backend needed to demo). */
let GUILD_FOLDERS: { id: string; name: string | null; color: string | null; position: number; guildIds: string[] }[] = [
  { id: 'gf_cs', name: 'CS турниры', color: null, position: 0, guildIds: ['g_navi', 'g_major'] },
];
let folderSeq = 1;

function makeDmChannel(peerId: string, last: { content: string; minutesAgo: number; author: 'me' | 'peer' }) {
  const peer = PEERS.find((p) => p.id === peerId)!;
  const lastAuthor = last.author === 'me' ? ME : peer;
  return {
    id: `dm_${peerId}`,
    type: 'DM' as const,
    directKey: `${peerId}__me_0`,
    name: null,
    topic: null,
    region: 'ru',
    createdAt: new Date(NOW - 7 * 24 * 3600_000).toISOString(),
    updatedAt: new Date(NOW - last.minutesAgo * MIN).toISOString(),
    directParticipants: [
      { channelId: `dm_${peerId}`, userId: ME.id, joinedAt: new Date(NOW - 7 * 24 * 3600_000).toISOString(), user: ME },
      { channelId: `dm_${peerId}`, userId: peer.id, joinedAt: new Date(NOW - 7 * 24 * 3600_000).toISOString(), user: peer },
    ],
    messages: [
      {
        id: `m_dm_${peerId}_last`,
        channelId: `dm_${peerId}`,
        authorId: lastAuthor.id,
        content: last.content,
        attachments: null,
        mentions: [],
        replyToId: null,
        replyTo: null,
        pinned: false,
        editedAt: null,
        deletedAt: null,
        createdAt: new Date(NOW - last.minutesAgo * MIN).toISOString(),
        author: lastAuthor,
        reactions: [],
      },
    ],
  };
}

/** Personal "Сейф" — a self-DM whose only participant is ME (isSelfVault). */
function makeVaultDm() {
  return {
    id: 'dm_vault_me',
    type: 'DM' as const,
    directKey: 'me_0__me_0',
    name: null,
    topic: null,
    region: 'ru',
    createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(),
    updatedAt: new Date(NOW).toISOString(),
    directParticipants: [
      { channelId: 'dm_vault_me', userId: ME.id, joinedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), user: ME },
    ],
    messages: [],
  };
}

function makeGroupDm() {
  const peers = PEERS.slice(0, 4);
  const participants = [ME, ...peers];
  return {
    id: 'gdm_team',
    type: 'GROUP_DM' as const,
    directKey: null,
    name: 'Команда 5×5',
    topic: null,
    iconUrl: null,
    memberPermissions: 63,
    region: 'ru',
    createdAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(),
    updatedAt: new Date(NOW - 6 * MIN).toISOString(),
    directParticipants: participants.map((u, idx) => ({
      channelId: 'gdm_team',
      userId: u.id,
      joinedAt: new Date(NOW - (30 - idx) * 24 * 3600_000).toISOString(),
      // ME is the OWNER (creator). Promote one peer to ADMIN so the
      // tabbed admin UI has both role examples to render.
      role:
        u.id === ME.id ? ('OWNER' as const) : idx === 1 ? ('ADMIN' as const) : ('MEMBER' as const),
      user: u,
    })),
    messages: [
      {
        id: 'm_gdm_last',
        channelId: 'gdm_team',
        authorId: peers[0]!.id,
        content: 'Кто на тренировку завтра?',
        attachments: null,
        mentions: [],
        replyToId: null,
        replyTo: null,
        pinned: false,
        editedAt: null,
        deletedAt: null,
        createdAt: new Date(NOW - 6 * MIN).toISOString(),
        author: peers[0]!,
        reactions: [],
      },
    ],
  };
}

const DMS = [
  makeGroupDm(),
  makeDmChannel('u_kostyl', { content: 'Сегодня в 21:00 5×5?', minutesAgo: 8, author: 'peer' }),
  makeDmChannel('u_b1ad', { content: 'Ок, кидай инвайт', minutesAgo: 35, author: 'me' }),
  makeDmChannel('u_dmnk', { content: 'Я ушёл спать, до завтра.', minutesAgo: 12 * 60, author: 'peer' }),
];

const MESSAGES_GENERAL = [
  {
    id: 'm_g_1',
    channelId: 'c_general',
    authorId: 'u_kostyl',
    content: 'Завтра турнир — кто в команде на 5×5? Сбор в 19:00 МСК.',
    attachments: null,
    mentions: [],
    replyToId: null,
    replyTo: null,
    pinned: true,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date(NOW - 90 * MIN).toISOString(),
    author: PEERS[0]!,
    reactions: [{ emoji: '🔥', count: 4, users: ['me_0', 'u_b1ad', 'u_makes', 'u_dmnk'] }],
  },
  {
    id: 'm_g_2',
    channelId: 'c_general',
    authorId: 'me_0',
    content: 'Я в деле. Скинул конфиг в #praks, гляньте обязательно.',
    attachments: null,
    mentions: [],
    replyToId: null,
    replyTo: null,
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date(NOW - 80 * MIN).toISOString(),
    author: ME,
    reactions: [],
  },
  {
    id: 'm_g_3',
    channelId: 'c_general',
    authorId: 'u_b1ad',
    content: '**глянул**. Сглажен прицел нормально, но _радар_ слишком мелкий. Скинь скрин hud_radar_scale.',
    attachments: null,
    mentions: ['me_0'],
    replyToId: 'm_g_2',
    replyTo: {
      id: 'm_g_2',
      content: 'Я в деле. Скинул конфиг в #praks, гляньте обязательно.',
      deletedAt: null,
      author: { id: 'me_0', username: 'tester', displayName: 'Тестер' },
    },
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date(NOW - 40 * MIN).toISOString(),
    author: PEERS[1]!,
    reactions: [{ emoji: '👍', count: 2, users: ['me_0', 'u_kostyl'] }],
  },
  {
    id: 'm_g_4',
    channelId: 'c_general',
    authorId: 'me_0',
    content: 'hud_radar_scale 0.7 — но это лично мне. Сам тестил долго.',
    attachments: null,
    mentions: [],
    replyToId: null,
    replyTo: null,
    pinned: false,
    editedAt: new Date(NOW - 25 * MIN).toISOString(),
    deletedAt: null,
    createdAt: new Date(NOW - 30 * MIN).toISOString(),
    author: ME,
    reactions: [],
  },
  {
    id: 'm_g_5',
    channelId: 'c_general',
    authorId: 'u_makes',
    content: 'Парни, демку посмотрите когда будет время — там b1ad затащил 1v4 на mirage 😮',
    attachments: null,
    mentions: [],
    replyToId: null,
    replyTo: null,
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date(NOW - 12 * MIN).toISOString(),
    author: PEERS[2]!,
    reactions: [
      { emoji: '🔥', count: 3, users: ['me_0', 'u_b1ad', 'u_kostyl'] },
      { emoji: '🎉', count: 1, users: ['u_kostyl'] },
    ],
  },
  {
    id: 'm_g_6',
    channelId: 'c_general',
    authorId: 'u_kostyl',
    content: 'Огонь. Кстати, @tester — выложи свой cfg отдельным сообщением, удобнее искать будет.',
    attachments: null,
    mentions: ['me_0'],
    replyToId: null,
    replyTo: null,
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date(NOW - 4 * MIN).toISOString(),
    author: PEERS[0]!,
    reactions: [],
  },
  // Demo: a forwarded message in v1 wire format. The body itself
  // encodes the "↪ Переслано …" header + a "> "-prefixed quote of the
  // original; MessageBody / parseForwarded() upgrade this into a
  // Telegram-style pill at render time.
  {
    id: 'm_g_7_fwd',
    channelId: 'c_general',
    authorId: 'me_0',
    content:
      '↪ Переслано · Доминик из #praks\n> Сегодня тренировка отменилась — переносим на завтра 20:00 МСК. Кто не сможет — пишите в лс.\nК завтрашнему мачу как раз пригодится.',
    attachments: null,
    mentions: [],
    replyToId: null,
    replyTo: null,
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: new Date(NOW - 1 * MIN).toISOString(),
    author: ME,
    reactions: [],
  },
];

const MEMBERS = [
  { guildId: 'g_cispracs', userId: 'me_0', nickname: null, joinedAt: new Date(NOW - 30 * 24 * 3600_000).toISOString(), roleIds: ['r_owner'], timeoutUntil: null, user: ME },
  ...PEERS.map((p) => ({
    guildId: 'g_cispracs',
    userId: p.id,
    nickname: null,
    joinedAt: new Date(NOW - 10 * 24 * 3600_000).toISOString(),
    roleIds: p.teamTag === 'NAVI' ? ['r_navi'] : p.teamTag === 'VP' ? ['r_vp'] : [],
    timeoutUntil: null,
    user: p,
  })),
];

const SETTINGS = createMockGuildSettings({
  channel: (id) => GUILD.channels.find((channel) => channel.id === id),
  roles: [
    // @everyone (id == guild id) is real on the server; without it the roles
    // screen and permission fold in mock mode differed from production.
    { id: 'g_cispracs', guildId: 'g_cispracs', name: '@everyone', color: 0, position: 0, permissions: DEFAULT_MEMBER_PERMISSIONS.toString(), isEveryone: true },
    { id: 'r_owner', guildId: 'g_cispracs', name: 'Owner', color: 0x22d3ee, position: 100, permissions: String((1n << 0n).toString()) },
    { id: 'r_navi', guildId: 'g_cispracs', name: 'NaVi', color: 0xeab308, position: 50, permissions: '0' },
    { id: 'r_vp', guildId: 'g_cispracs', name: 'Virtus.pro', color: 0xf97316, position: 40, permissions: '0' },
  ],
});

// Keep demo payloads aligned with server-owned privacy. Never send the preference
// on a public author; only /me returns it.
function publicMockUser(user: MockUser, direct = false) {
  const { showTeamInDms, ...publicUser } = user;
  return { ...publicUser, teamTag: direct && showTeamInDms === false ? null : user.teamTag ?? null };
}
function publicMockMessage<T extends { author: MockUser }>(message: T, direct: boolean) {
  return { ...message, author: publicMockUser(message.author, direct) };
}
function publicMockDm(channel: (typeof DMS)[number]) {
  return { ...channel,
    directParticipants: channel.directParticipants.map(p => ({ ...p, user: publicMockUser(p.user, true) })),
    messages: channel.messages.map(m => publicMockMessage(m, true)),
  };
}

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

function routeFor(url: string, init: RequestInit | undefined): Response {
  const u = new URL(url, location.origin);
  const path = u.pathname;
  const method = (init?.method || 'GET').toUpperCase();

  if (path === '/api/me/avatar' && method === 'POST') {
    // Bytes-only avatar write-back (§2.2/§4.8d). Real shape: the server
    // forwards to cispracs and answers with the canonical URL they store it
    // at. Mock mode never has bytes to forward, so it returns a stable
    // cispracs-shaped URL — enough for the UI to render the post-upload state
    // and for E2E to drive the flow with no backend.
    const avatarUrl = 'https://cispracs.ru/uploads/user-avatars/mock0000.png';
    ME.avatarUrl = avatarUrl;
    return jsonResponse({ avatarUrl });
  }
  if (path === '/api/me' && method === 'PATCH') {
    // Mirror the real route: PATCH returns the bare (updated) user, not the
    // wrapped boot payload. Merge the patched fields so status/displayName
    // reflect locally during mock sessions.
    // FormData never reaches this route any more (avatar bytes go to
    // /api/me/avatar), so the body is always JSON here.
    const patch =
      init?.body && typeof init.body === 'string'
        ? (JSON.parse(init.body) as Partial<typeof ME>)
        : {};
    Object.assign(ME, patch);
    return jsonResponse(ME);
  }
  if (path === '/api/me') return jsonResponse({ user: ME, guilds: GUILDS, guildFolders: GUILD_FOLDERS, dm: DMS.map(publicMockDm) });
  if (path === '/api/rtc/config') return jsonResponse({ mediaBackend: 'sfu', iceServers: [] });
  if (path === '/api/me/blocks') return jsonResponse({ blocks: [] });
  if (path === '/api/me/friends' && method === 'GET') {
    return jsonResponse({ friends: [], incoming: [], outgoing: [] });
  }
  if (path === '/api/me/dm-folders' && method === 'GET') return jsonResponse({ folders: [] });
  if (path === '/api/me/search' && method === 'GET') return jsonResponse({ messages: [] });
  if (path === '/api/reports' && method === 'GET') return jsonResponse(mockReportQueue(u.searchParams));
  const accountAnswer = handleMockAccount(path, method);
  if (accountAnswer !== undefined) return jsonResponse(accountAnswer);
  const muteAnswer = handleMockMutes(path, method, typeof init?.body === 'string' ? init.body : undefined);
  if (muteAnswer !== undefined) return jsonResponse(muteAnswer);
  if (path === '/api/me/deleted-guilds' && method === 'GET') return jsonResponse({ guilds: [] });
  const mutualGuildsMatch = path.match(/^\/api\/users\/([^/]+)\/mutual-guilds$/);
  if (mutualGuildsMatch && method === 'GET') {
    const guilds = mutualGuildsMatch[1] === ME.id ? [] : GUILDS.slice(0, mutualGuildsMatch[1] === 'u_kostyl' ? 4 : 2);
    return jsonResponse({ guilds: guilds.map(({ id, name, iconUrl }) => ({ id, name, iconUrl })) });
  }
  if (path === '/api/me/push/config') return jsonResponse({ enabled: false, publicKey: null });
  if (path === '/api/me/vault' && method === 'POST') return jsonResponse(publicMockDm(makeVaultDm()));
  if (path === '/api/dms') return jsonResponse({ dm: DMS.map(publicMockDm) });
  const groupCandidates = /^\/api\/dms\/([^/]+)\/candidates$/.exec(path);
  if (groupCandidates && method === 'GET') {
    const group = DMS.find(channel => channel.id === groupCandidates[1]);
    const members = new Set(group?.directParticipants.map(participant => participant.userId));
    return jsonResponse({ users: PEERS.filter(user => !members.has(user.id)).map(user => publicMockUser(user, true)) });
  }

  // ── server folders (mutable mock state so the gestures work) ──
  if (path === '/api/me/guild-folders' && method === 'GET') {
    return jsonResponse({ folders: GUILD_FOLDERS });
  }
  if (path === '/api/me/guild-folders' && method === 'POST') {
    const body = init?.body ? (JSON.parse(init.body as string) as { guildIds: string[]; name?: string; color?: string }) : { guildIds: [] };
    const folder = {
      id: `gf_${++folderSeq}`,
      name: body.name ?? null,
      color: body.color ?? null,
      position: GUILD_FOLDERS.length,
      guildIds: body.guildIds,
    };
    // A guild lives in one folder — pull the members out of any other folder.
    GUILD_FOLDERS = GUILD_FOLDERS.map((f) => ({ ...f, guildIds: f.guildIds.filter((g) => !body.guildIds.includes(g)) }));
    GUILD_FOLDERS.push(folder);
    return jsonResponse(folder);
  }
  const folderEntryMatch = path.match(/^\/api\/me\/guild-folders\/([^/]+)\/entries\/([^/]+)$/);
  if (folderEntryMatch) {
    const [, fid, gid] = folderEntryMatch;
    if (method === 'PUT') {
      GUILD_FOLDERS = GUILD_FOLDERS.map((f) =>
        f.id === fid
          ? { ...f, guildIds: f.guildIds.includes(gid!) ? f.guildIds : [...f.guildIds, gid!] }
          : { ...f, guildIds: f.guildIds.filter((g) => g !== gid) },
      );
      return jsonResponse({ ok: true });
    }
    if (method === 'DELETE') {
      const folder = GUILD_FOLDERS.find((f) => f.id === fid);
      const remaining = (folder?.guildIds.filter((g) => g !== gid) ?? []);
      if (remaining.length < 2) GUILD_FOLDERS = GUILD_FOLDERS.filter((f) => f.id !== fid);
      else GUILD_FOLDERS = GUILD_FOLDERS.map((f) => (f.id === fid ? { ...f, guildIds: remaining } : f));
      return jsonResponse({ ok: true, dissolved: remaining.length < 2 });
    }
  }
  const folderMatch = path.match(/^\/api\/me\/guild-folders\/([^/]+)$/);
  if (folderMatch) {
    const fid = folderMatch[1];
    if (method === 'DELETE') {
      GUILD_FOLDERS = GUILD_FOLDERS.filter((f) => f.id !== fid);
      return jsonResponse({ ok: true });
    }
    if (method === 'PATCH') {
      const patch = init?.body ? (JSON.parse(init.body as string) as { name?: string | null; color?: string | null }) : {};
      let updated = GUILD_FOLDERS.find((f) => f.id === fid)!;
      GUILD_FOLDERS = GUILD_FOLDERS.map((f) => (f.id === fid ? (updated = { ...f, ...patch }) : f));
      return jsonResponse(updated);
    }
  }

  // Roles and channel permissions keep their state for the session.
  const settingsReply = SETTINGS.handle(path, method, init?.body ? JSON.parse(String(init.body)) : null);
  if (settingsReply) {
    return settingsReply.status === 204
      ? new Response(null, { status: 204 })
      : jsonResponse(settingsReply.body, settingsReply.status);
  }

  // The big server's roster, detail first page + /members like the real API.
  if (BIG && path === `/api/guilds/${BIG_GUILD_ID}` && method === 'GET') return jsonResponse(BIG.detail());
  if (BIG && path === `/api/guilds/${BIG_GUILD_ID}/members` && method === 'GET') {
    return jsonResponse(BIG.page(u.searchParams.get('after'), Number(u.searchParams.get('limit') ?? 1000)));
  }

  // Guild detail
  const guildMatch = path.match(/^\/api\/guilds\/([^/]+)$/);
  if (guildMatch) {
    return jsonResponse({
      ...GUILD,
      channels: GUILD.channels.map((channel) => ({ ...channel, overrides: SETTINGS.overrides(channel.id) })),
      members: MEMBERS.map(m => ({ ...m, user: publicMockUser(m.user) })),
      roles: SETTINGS.roles(GUILD.id),
      bans: [],
      auditLog: [],
    });
  }

  // Bans + audit-log — both routes return empty arrays in the mock.
  if (path.match(/^\/api\/guilds\/[^/]+\/bans$/) && method === 'GET') {
    return jsonResponse({ bans: [] });
  }
  if (path.match(/^\/api\/guilds\/[^/]+\/audit-logs?$/) && method === 'GET') {
    return jsonResponse({ logs: [] });
  }
  if (path.match(/^\/api\/guilds\/[^/]+\/emojis$/) && method === 'GET') {
    return jsonResponse({ emojis: [] });
  }

  // Messages list
  const listMatch = path.match(/^\/api\/channels\/([^/]+)\/messages$/);
  if (listMatch) {
    if (method === 'POST') {
      // Echo the send back as if it persisted.
      const body = init?.body ? JSON.parse(String(init.body)) : {};
      const created = {
        id: `m_local_${Date.now()}`,
        channelId: listMatch[1],
        authorId: ME.id,
        content: body.content || ' ',
        attachments: body.attachments ?? null,
        mentions: body.mentions ?? [],
        replyToId: body.replyToId ?? null,
        replyTo: null,
        pinned: false,
        editedAt: null,
        deletedAt: null,
        createdAt: new Date().toISOString(),
        author: ME,
        reactions: [],
      };
      return jsonResponse(publicMockMessage(created, DMS.some(d => d.id === listMatch[1])), 201);
    }
    const id = listMatch[1];
    const dm = DMS.find((d) => d.id === id);
    const messages = id === 'c_general'
      ? [...MESSAGES_GENERAL].reverse()
      : dm
        ? [...dm.messages].reverse()
        : [];
    return jsonResponse({ messages: messages.map(m => publicMockMessage(m, !!dm)) });
  }

  // Pinned
  const pinnedMatch = path.match(/^\/api\/channels\/([^/]+)\/pinned$/);
  if (pinnedMatch) {
    const pinned = MESSAGES_GENERAL.filter((m) => m.channelId === pinnedMatch[1] && m.pinned);
    return jsonResponse({ messages: pinned.map(m => publicMockMessage(m, false)) });
  }

  // Thread comments ("Комментарии") — none yet. The catch-all `{ ok: true }`
  // below has no `comments` list, so opening a thread in ?mock=full crashed
  // the app into the error screen.
  if (path.match(/^\/api\/messages\/[^/]+\/thread$/) && method === 'GET') {
    return jsonResponse({ comments: [] });
  }

  // Read receipts
  if (path.match(/^\/api\/channels\/[^/]+\/read$/)) {
    if (method === 'POST') return new Response(null, { status: 204 });
    return jsonResponse({ markers: [] });
  }

  // Reactions — accept silently.
  if (path.match(/^\/api\/messages\/[^/]+\/reactions\//)) {
    return new Response(null, { status: 204 });
  }

  // Edit / delete / pin — accept silently.
  if (path.match(/^\/api\/messages\/[^/]+$/) && method === 'PATCH') {
    return jsonResponse({});
  }
  if (path.match(/^\/api\/messages\/[^/]+$/) && method === 'DELETE') {
    return new Response(null, { status: 204 });
  }

  // Default: pretend everything else 200s with empty payload so the UI
  // doesn't surface scary errors. Said out loud: a screen that reads a list
  // from `{ ok: true }` crashes (the «Жалобы» tab did), and the UI gallery
  // collects these warnings as missing mocks.
  console.warn(`[mock] no mock for ${method} ${path}`);
  return jsonResponse({ ok: true });
}

export function installMockMode(): void {
  if (typeof window === 'undefined') return;
  if (!import.meta.env.DEV) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mock') !== 'full') return;
  const voiceCount = Number(params.get('mockVoice'));
  if (Number.isInteger(voiceCount) && voiceCount > 0 && voiceCount <= 32) {
    void import('@/stores/dataStore').then(({ useDataStore }) => {
      const users = [ME, ...PEERS];
      useDataStore.getState().setVoiceRooms({ c_main_voice: Array.from({ length: voiceCount }, (_, index) => ({
        userId: users[index]?.id ?? `voice_guest_${index + 1}`,
        channelId: 'c_main_voice', selfMute: false, selfDeaf: false,
        serverMute: false, serverDeaf: false, videoOn: false, screenOn: false, joinedAt: NOW,
      })) }, null);
    });
  }

  console.info('[mock] full preview mode — all /api/* are stubbed locally');
  // Loading and error states for the UI gallery: `mockHold=<part>` leaves
  // matching requests pending forever, `mockFail=<part>` answers them 500.
  const hold = params.get('mockHold');
  const fail = params.get('mockFail');
  const origFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    if (url.startsWith('/api/') || url.includes('/api/')) {
      if (hold && url.includes(hold)) return new Promise<Response>(() => {});
      if (fail && url.includes(fail)) return Promise.resolve(jsonResponse({ error: 'internal_error' }, 500));
      try {
        return Promise.resolve(routeFor(url, init));
      } catch (err) {
        console.warn('[mock] route error', url, err);
        return Promise.resolve(jsonResponse({ error: 'mock_error' }, 500));
      }
    }
    return origFetch(input as RequestInfo, init);
  }) as typeof fetch;

  // Neutralize the Socket.io connection — it'd otherwise keep retrying
  // against ws://localhost:4001/ws.
  (window as unknown as { __BUNKER_MOCK__: boolean }).__BUNKER_MOCK__ = true;
}
